import { useMemo, useState } from 'react'
import { Player } from '../types'
import { Sparkles, FileText, Copy, Check, Download, RefreshCw, Loader2 } from 'lucide-react'
import { sanitizeHTML } from '../utils/security'

interface ReportSection {
  title: string
  body: string
}

interface ScoutaAnalystReportProps {
  player: Player
}

const num = (v?: number) => (typeof v === 'number' && isFinite(v) ? v : 0)
const fmtRate = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2))

const computeAge = (player: Player): number | null => {
  if (player.age && player.age > 0) return player.age
  if (player.date_of_birth) {
    const dob = new Date(player.date_of_birth)
    if (!isNaN(dob.getTime())) {
      const diff = Date.now() - dob.getTime()
      const age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000))
      if (age > 0 && age < 60) return age
    }
  }
  return null
}

const positionLine = (player: Player): string => {
  const seen: string[] = []
  const push = (p?: string) => {
    const clean = (p || '').trim()
    if (clean && clean.toUpperCase() !== 'N/A' && !seen.some(s => s.toUpperCase() === clean.toUpperCase())) {
      seen.push(clean)
    }
  }
  push(player.main_position)
  ;(player.positions || []).forEach(push)
  return seen.length > 0 ? seen.join(' / ') : 'Position not recorded'
}

const clubLine = (player: Player): string => {
  const club = sanitizeHTML(player.club || '').trim()
  if (player.affiliation_type === 'free_agent' || !club) return 'currently unattached'
  return `currently with ${club}`
}

const buildSections = (player: Player): ReportSection[] => {
  const name = sanitizeHTML(player.name || 'Unnamed player')
  const age = computeAge(player)
  const positions = positionLine(player)
  const league = sanitizeHTML(player.league || '').trim()
  const location = [sanitizeHTML(player.city || '').trim(), sanitizeHTML(player.state || '').trim()]
    .filter(Boolean)
    .join(', ')

  const apps = num(player.apps)
  const mins = num(player.mins)
  const goals = num(player.goals)
  const assists = num(player.assists)
  const contributions = goals + assists
  const shots = num(player.shots)
  const kp = num(player.kp)
  const dr = num(player.dr)
  const du = num(player.du)
  const yellows = num(player.yellow_cards)
  const reds = num(player.red_cards)
  const cleanSheets = num(player.clean_sheets)

  const summaryParts: string[] = []
  summaryParts.push(
    `${name}${age ? ` (${age} years old)` : ''} is a ${positions.toLowerCase()} operating ${clubLine(player)}${
      league ? ` in the ${league}` : ''
    }${location ? ` — based in ${location}.` : '.'}`
  )
  const foot = (player.foot || '').trim()
  const physicalBits: string[] = []
  if (foot) physicalBits.push(`${foot}-footed`)
  if (player.height) physicalBits.push(`${player.height} cm tall`)
  if (player.weight) physicalBits.push(`${player.weight} kg`)
  if (physicalBits.length > 0) summaryParts.push(`Physical profile: ${physicalBits.join(', ')}.`)
  if (player.biography) summaryParts.push(sanitizeHTML(player.biography).trim())
  if (player.status && player.status !== 'Active') summaryParts.push(`Current availability status: ${player.status}.`)

  const snapshot: string[] = []
  if (apps > 0) {
    snapshot.push(
      `${apps} appearance${apps === 1 ? '' : 's'} (${mins} minutes) with ${goals} goal${goals === 1 ? '' : 's'} and ${assists} assist${assists === 1 ? '' : 's'} — ${contributions} goal contribution${contributions === 1 ? '' : 's'} total.`
    )
    snapshot.push(
      `Rates: ${fmtRate(goals / apps)} goals per game, ${fmtRate(contributions / apps)} contributions per game${mins > 0 ? `, one contribution every ${fmtRate(mins / Math.max(contributions, 1))} minutes` : ''}.`
    )
    const supporting = [
      shots > 0 ? `${shots} shots` : null,
      kp > 0 ? `${kp} key passes` : null,
      dr > 0 ? `${dr} successful dribbles` : null,
      du > 0 ? `${du} duels won` : null,
    ].filter(Boolean)
    if (supporting.length > 0) snapshot.push(`Involvement: ${supporting.join(', ')}.`)
    const discipline = [
      yellows > 0 ? `${yellows} yellow card${yellows === 1 ? '' : 's'}` : null,
      reds > 0 ? `${reds} red card${reds === 1 ? '' : 's'}` : null,
    ].filter(Boolean)
    snapshot.push(
      `Discipline: ${discipline.length > 0 ? discipline.join(', ') : 'clean record'}${
        cleanSheets > 0 ? ` • ${cleanSheets} clean sheet${cleanSheets === 1 ? '' : 's'}` : ''
      }.`
    )
  } else {
    snapshot.push('No competitive appearances recorded on the platform yet — performance data will accumulate as matches are logged.')
    if (shots || kp || dr || du) {
      snapshot.push(`Training / friendly involvement logged: ${shots} shots, ${kp} key passes, ${dr} dribbles, ${du} duels won.`)
    }
  }

  const career: string[] = []
  const careerEntries = player.career_history || []
  const compEntries = player.competition_performances || []
  if (careerEntries.length > 0) {
    const latest = careerEntries[0]
    career.push(
      `${careerEntries.length} career entry${careerEntries.length === 1 ? '' : 's'} on record${latest?.club ? `, most recently ${sanitizeHTML(latest.club)} (${sanitizeHTML(latest.years || '')})` : ''}.`
    )
  }
  if (compEntries.length > 0) {
    const compGoals = compEntries.reduce((sum, c) => sum + num(c.goals), 0)
    const compAssists = compEntries.reduce((sum, c) => sum + num(c.assists), 0)
    career.push(
      `${compEntries.length} competition performance${compEntries.length === 1 ? '' : 's'} logged (${compGoals} goals, ${compAssists} assists in registered competitions).`
    )
  }
  if (player.competitions && player.competitions.length > 0) {
    career.push(`Registered competitions: ${player.competitions.map(c => sanitizeHTML(String(c))).join(', ')}.`)
  }
  if (career.length === 0) career.push('No career history or competition records logged yet.')

  const status: string[] = []
  status.push(
    [
      player.is_verified ? 'Platform verified' : null,
      player.club_verified ? 'club verified' : null,
      player.academy_verified ? 'academy verified' : null,
      player.is_top_talent ? 'flagged as top talent' : null,
    ].filter(Boolean).join(' • ') || 'Not yet verified on the platform'
  )
  status.push(
    player.has_agent
      ? `Represented${player.agent_name ? ` by ${sanitizeHTML(player.agent_name)}` : ' by an agent'}.`
      : 'No agent on record — direct contact available.'
  )
  const availability = [
    player.available_for_transfer ? 'open to transfer' : null,
    player.available_for_trials ? 'open to trials' : null,
    player.seeking_club ? 'seeking a club' : null,
    player.seeking_academy ? 'seeking academy placement' : null,
  ].filter(Boolean)
  if (availability.length > 0) status.push(`Market status: ${availability.join(', ')}.`)
  else if (player.status) status.push(`Registration status: ${player.status}.`)

  return [
    { title: 'Scouting Summary', body: summaryParts.filter(Boolean).join(' ') },
    { title: 'Performance Snapshot', body: snapshot.join(' ') },
    { title: 'Career & Competitions', body: career.join(' ') },
    { title: 'Status & Availability', body: status.join(' ') },
  ]
}

const buildPlainText = (player: Player, sections: ReportSection[], generatedAt: Date): string => {
  const name = sanitizeHTML(player.name || 'Unnamed player')
  const header = [
    'SCOUTA ANALYST REPORT',
    `${name} • ${positionLine(player)} • ${sanitizeHTML(player.club || 'Unattached') || 'Unattached'}`,
    `Generated: ${generatedAt.toLocaleString()}`,
    'Source: Scouta Intelligence (auto-generated profile summary)',
  ].join('\n')
  const body = sections.map(s => `${s.title.toUpperCase()}\n${s.body}`).join('\n\n')
  return `${header}\n\n${body}\n`
}

function ScoutaAnalystReport({ player }: ScoutaAnalystReportProps) {
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)

  const sections = useMemo(() => (generatedAt ? buildSections(player) : []), [player, generatedAt])

  const handleGenerate = () => {
    if (generating) return
    setGenerating(true)
    window.setTimeout(() => {
      setGeneratedAt(new Date())
      setGenerating(false)
    }, 700)
  }

  const plainText = useMemo(
    () => (generatedAt ? buildPlainText(player, sections, generatedAt) : ''),
    [player, sections, generatedAt]
  )

  const handleCopy = async () => {
    if (!plainText) return
    try {
      await navigator.clipboard.writeText(plainText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      const area = document.createElement('textarea')
      area.value = plainText
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      document.body.removeChild(area)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    }
  }

  const handleDownload = () => {
    if (!plainText) return
    const safeName = (player.name || 'player').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
    const blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `scouta-analyst-report-${safeName}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#7cff01]/10 border border-[#7cff01]/30 flex items-center justify-center">
            <Sparkles size={16} className="text-[#7cff01]" />
          </div>
          <div>
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Scouta Analyst Report</p>
            <p className="text-xs text-gray-400 mt-0.5">One-click generated profile summary</p>
          </div>
        </div>
        {generatedAt && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-gray-800 text-[10px] font-black uppercase tracking-widest text-gray-300 hover:border-[#7cff01]/40 hover:text-white transition"
            >
              {copied ? <Check size={12} className="text-[#7cff01]" /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-gray-800 text-[10px] font-black uppercase tracking-widest text-gray-300 hover:border-[#7cff01]/40 hover:text-white transition"
            >
              <Download size={12} />
              Save
            </button>
            <button
              onClick={handleGenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-gray-800 text-[10px] font-black uppercase tracking-widest text-gray-300 hover:border-[#7cff01]/40 hover:text-white transition"
              title="Regenerate"
            >
              <RefreshCw size={12} />
            </button>
          </div>
        )}
      </div>

      {!generatedAt && !generating && (
        <div className="border border-dashed border-gray-800 rounded-2xl py-10 flex flex-col items-center gap-4 bg-[#0d1117]/60">
          <FileText size={28} className="text-gray-600" />
          <p className="text-xs text-gray-500 max-w-sm text-center leading-relaxed">
            Generate an instant Scouta-authored summary of {sanitizeHTML(player.name || 'this player')} — position,
            performance, career records and market status in one report.
          </p>
          <button
            onClick={handleGenerate}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#7cff01] text-black text-xs font-black uppercase tracking-widest hover:opacity-90 transition"
          >
            <Sparkles size={14} />
            Generate Report
          </button>
        </div>
      )}

      {generating && (
        <div className="border border-gray-800 rounded-2xl py-10 flex flex-col items-center gap-3 bg-[#0d1117]/60">
          <Loader2 size={24} className="text-[#7cff01] animate-spin" />
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Analysing profile…</p>
        </div>
      )}

      {generatedAt && !generating && (
        <div className="border border-gray-800 rounded-2xl bg-[#0d1117] p-5">
          <div className="flex items-center justify-between gap-3 pb-3 mb-4 border-b border-gray-800">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7cff01]">
              Report — {sanitizeHTML(player.name || 'Player')}
            </p>
            <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
              {generatedAt.toLocaleDateString()} • Scouta Intelligence
            </p>
          </div>
          <div className="space-y-4">
            {sections.map(section => (
              <div key={section.title}>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5">{section.title}</p>
                <p className="text-[13px] leading-relaxed text-gray-300">{section.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 pt-3 border-t border-gray-800 text-[10px] text-gray-600 leading-relaxed">
            Auto-generated by Scouta Intelligence from on-platform profile data. For live scouting notes, use the
            Report tool on the Rating tab.
          </p>
        </div>
      )}
    </div>
  )
}

export default ScoutaAnalystReport
